import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Landmark, Plus, Edit, Trash2, Star, RefreshCw, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BankAccounts = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const [form, setForm] = useState({
    label: '',
    iban: '',
    bic: '',
    titulaire: '',
    banque: '',
    is_default: false
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/bank-accounts`, { headers });
      setAccounts(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des comptes bancaires');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const resetForm = () => {
    setForm({ label: '', iban: '', bic: '', titulaire: '', banque: '', is_default: false });
    setEditingAccount(null);
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEdit = (account) => {
    setEditingAccount(account);
    setForm({
      label: account.label || '',
      iban: account.iban || '',
      bic: account.bic || '',
      titulaire: account.titulaire || '',
      banque: account.banque || '',
      is_default: account.is_default || false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.label || !form.iban) {
      toast.error('Le libelle et l\'IBAN sont obligatoires');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (editingAccount) {
        await axios.put(`${API_URL}/bank-accounts/${editingAccount.id}`, form, { headers });
        toast.success('Compte bancaire mis a jour');
      } else {
        await axios.post(`${API_URL}/bank-accounts`, form, { headers });
        toast.success('Compte bancaire créé');
      }
      setShowDialog(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Supprimer le compte "${account.label}" ?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/bank-accounts/${account.id}`, { headers });
      toast.success('Compte supprime');
      fetchAccounts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (account) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/bank-accounts/${account.id}/set-default`, {}, { headers });
      toast.success(`"${account.label}" defini comme compte par defaut`);
      fetchAccounts();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const formatIBAN = (iban) => {
    if (!iban) return '-';
    return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim().toUpperCase();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Comptes bancaires</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gerez les comptes bancaires de votre structure
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAccounts}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau compte
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {accounts.length === 0 ? (
            <div className="text-center py-12">
              <Landmark className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg font-medium">Aucun compte bancaire</p>
              <p className="text-slate-400 text-sm mt-1">Ajoutez votre premier compte bancaire</p>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un compte
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Libelle</TableHead>
                  <TableHead>IBAN</TableHead>
                  <TableHead>BIC</TableHead>
                  <TableHead>Titulaire</TableHead>
                  <TableHead>Banque</TableHead>
                  <TableHead>Par defaut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">{account.label}</TableCell>
                    <TableCell className="font-mono text-sm">{formatIBAN(account.iban)}</TableCell>
                    <TableCell className="font-mono text-sm">{account.bic || '-'}</TableCell>
                    <TableCell>{account.titulaire || '-'}</TableCell>
                    <TableCell>{account.banque || '-'}</TableCell>
                    <TableCell>
                      {account.is_default ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Par defaut
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(account)}
                          className="text-slate-400 hover:text-blue-600"
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Definir
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(account)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(account)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Modifier le compte bancaire' : 'Nouveau compte bancaire'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Libelle *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="ex: Compte principal"
              />
            </div>

            <div className="space-y-2">
              <Label>IBAN *</Label>
              <Input
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value.replace(/\s/g, '').toUpperCase() })}
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                className="font-mono"
                maxLength={34}
              />
            </div>

            <div className="space-y-2">
              <Label>BIC / SWIFT</Label>
              <Input
                value={form.bic}
                onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })}
                placeholder="BNPAFRPP"
                className="font-mono"
                maxLength={11}
              />
            </div>

            <div className="space-y-2">
              <Label>Titulaire du compte</Label>
              <Input
                value={form.titulaire}
                onChange={(e) => setForm({ ...form, titulaire: e.target.value })}
                placeholder="Nom du titulaire"
              />
            </div>

            <div className="space-y-2">
              <Label>Banque</Label>
              <Input
                value={form.banque}
                onChange={(e) => setForm({ ...form, banque: e.target.value })}
                placeholder="Nom de la banque"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
              Annuler
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave}>
              {editingAccount ? 'Mettre à jour' : 'Créer le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccounts;
